import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Shield, Search, Filter } from "lucide-react";
import { api } from "@/api/client";

interface AuditLogItem {
  id: string;
  document_id: string;
  document_judul: string;
  user_id: string;
  username: string;
  aksi: string;
  ip_address: string;
  keterangan: string;
  timestamp: string;
}

interface AuditLogResponse {
  items: AuditLogItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const ACTION_COLORS: Record<string, string> = {
  VIEW: "bg-blue-50 text-blue-700",
  DOWNLOAD: "bg-green-50 text-green-700",
  CREATE: "bg-purple-50 text-purple-700",
  EDIT: "bg-yellow-50 text-yellow-700",
  DELETE: "bg-red-50 text-red-700",
  APPROVE: "bg-emerald-50 text-emerald-700",
  REJECT: "bg-orange-50 text-orange-700",
  UPLOAD: "bg-indigo-50 text-indigo-700",
  LOGIN: "bg-gray-50 text-gray-700",
};

export default function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [filterAction, setFilterAction] = useState("");
  const [activeTab, setActiveTab] = useState<"access" | "login">("access");

  const { data: accessLogs, isLoading: accessLoading } = useQuery<AuditLogResponse>({
    queryKey: ["audit", "logs", page, filterAction],
    queryFn: () =>
      api.get("/audit/logs", {
        page,
        page_size: 50,
        action: filterAction || undefined,
      }),
    enabled: activeTab === "access",
  });

  const { data: loginHistory, isLoading: loginLoading } = useQuery<any>({
    queryKey: ["audit", "login-history", page],
    queryFn: () => api.get("/audit/login-history", { page, page_size: 50 }),
    enabled: activeTab === "login",
  });

  const isLoading = activeTab === "access" ? accessLoading : loginLoading;
  const currentData = activeTab === "access" ? accessLogs : loginHistory;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-gray-700" />
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        </div>
        <p className="text-sm text-gray-500 mt-0.5">
          Rekam jejak akses dan aktivitas dokumen
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => { setActiveTab("access"); setPage(1); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "access" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          Log Akses Dokumen
        </button>
        <button
          onClick={() => { setActiveTab("login"); setPage(1); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "login" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          Riwayat Login
        </button>
      </div>

      {/* Filters */}
      {activeTab === "access" && (
        <div className="flex gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filterAction}
              onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
              className="pl-9 pr-8 py-2 rounded-lg border border-gray-300 text-sm focus:border-blue-500"
            >
              <option value="">Semua Aksi</option>
              {["VIEW", "DOWNLOAD", "CREATE", "EDIT", "DELETE", "APPROVE", "REJECT", "UPLOAD"].map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              {activeTab === "access" ? (
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Waktu</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Pengguna</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Aksi</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Dokumen</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">IP Address</th>
                </tr>
              ) : (
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Waktu</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Pengguna</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">IP Address</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Keterangan</th>
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activeTab === "access" &&
                (accessLogs?.items ?? []).map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString("id-ID")}
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-medium">{log.username}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ACTION_COLORS[log.aksi] ?? "bg-gray-100 text-gray-700"}`}>
                        {log.aksi}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                      {log.document_judul ?? log.document_id}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono">{log.ip_address}</td>
                  </tr>
                ))}
              {activeTab === "login" &&
                (loginHistory?.items ?? []).map((log: any) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono whitespace-nowrap">
                      {new Date(log.login_at).toLocaleString("id-ID")}
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-medium">{log.username ?? "Unknown"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${log.is_success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {log.is_success ? "Berhasil" : "Gagal"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono">{log.ip_address}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{log.failure_reason ?? "-"}</td>
                  </tr>
                ))}
              {(currentData?.items ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400 text-sm">
                    Tidak ada data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {currentData && currentData.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-gray-500">
            Halaman {page} dari {currentData.total_pages} ({currentData.total} records)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-50 hover:bg-gray-50"
            >
              Sebelumnya
            </button>
            <button
              onClick={() => setPage((p) => Math.min(currentData.total_pages, p + 1))}
              disabled={page === currentData.total_pages}
              className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-50 hover:bg-gray-50"
            >
              Berikutnya
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
